// src/pages/HomeView/components/NewsFeed/MyCardsSection.tsx
import React, { useState, useEffect, useRef } from 'react';
import { useSocketConnectionId } from '@/contexts/SocketContext';
import { User, Tag, Users, CheckSquare } from 'lucide-react';
import { homeService } from '../../../../services/homeService';
import { authStorage } from '../../../../lib/storage';
import { config as envConfig } from '@/config/env';
import ConversationDetailsView from '@/components/ConversationDetails/ConversationDetailsView';
import type {
  ConversationDetail as ConversationDetailType,
  DashboardGroupConversationsResponse,
} from '../../../../services/homeService';
import type { ApiConfig, AppConfig } from '@/components/ConversationItem/types';
import { conversationBus } from '@/lib/conversationBus';
import { readViewCache, writeViewCache } from '@/lib/viewCache';

const MYCARDS_CACHE_KEY = 'C2_Cache_Home_MyCards';

/** Cards cached per section: enough for the first screen. */
const MYCARDS_CACHE_LIMIT = 15;

const SHOW_COMPLETED_STORAGE_KEY = 'C2_ShowCompleted_Home';

const readShowCompleted = (): boolean =>
{
  try
  {
    return localStorage.getItem(SHOW_COMPLETED_STORAGE_KEY) === 'true';
  }
  catch
  {
    return false;
  }
};

/** Removes finished cards from every group. */
const hideCompleted = <T extends { conversations: ConversationDetailType[] }>(groups: T[]): T[] =>
  groups.map(group => ({
    ...group,
    conversations: group.conversations.filter(conv => !conv.Finished),
  }));

// Helper function to get dynamic API configuration from session
const getApiConfig = (): ApiConfig & {
  refreshToken: string;
  ExternalData: string;
  SocketServer: string;
  TenancyName: string;
  PersonId: string;
} =>
{
  const token = authStorage.getAccessToken() || '';
  const personId = authStorage.getPersonId() || '0';

  return {
    url: envConfig.api.baseUrl,
    token: token,
    refreshToken: '',
    ExternalData: '',
    SocketServer: envConfig.socket.server,
    TenancyName: envConfig.tenant.defaultName,
    PersonId: personId,
  };
};

// Helper function to get dynamic App configuration
const getAppConfig = (): AppConfig & {
  buildVersion?: string;
  webChaturl?: string;
  appPrefix?: string;
} =>
{
  const webUrl = import.meta.env.VITE_WEB_URL || 'http://localhost/Corgee.WebClient/';
  const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost/Corgee.Web/';
  return {
    weburl: webUrl,
    apiurl: apiUrl,
    buildVersion: '1.0.0',
    webChaturl: webUrl + '/CHAT',
    appPrefix: '',
  };
};

// Storage Data Configuration - reads from real session/localStorage
const getStorageData = () =>
{
  let data: Record<string, any> = {};
  try
  {
    data = JSON.parse(sessionStorage.getItem('C2_data') || '{}');
  }
  catch
  {
    /* ignore */
  }

  let allLayers: any[] = [];
  try
  {
    allLayers = JSON.parse(sessionStorage.getItem('C2_AllLayers') || '[]');
  }
  catch
  {
    /* ignore */
  }

  let layersToHide: any[] = [];
  try
  {
    layersToHide = JSON.parse(sessionStorage.getItem('C2_LayersToHide') || '[]');
  }
  catch
  {
    /* ignore */
  }

  return {
    data,
    allLayers,
    layersToHide,
    layerId: parseInt(localStorage.getItem('C2_layerid') || '0'),
    conv_maxAllowedUpload: 20480000,
    enableChat: localStorage.getItem('C2_enableChat') === 'true',
    personid: parseInt(localStorage.getItem('C2_personid') || '0'),
  };
};

// Menu Configuration
const getConfigMenus = () => ({
  conversation: [
    'goto-project',
    'close-infowindow',
    'opendetails-newtab',
    'mark-redflag',
    'mark-important',
    'follow-conv',
    'copylink-to-clipboard',
    'create-itemtype',
    'submit-time',
  ],
  reply: ['mark-favorite', 'repost', 'edit', 'delete', 'create-itemtype'],
});

// Real Item Types Configuration
const realItemTypes = {
  Users: {
    Name: 'People',
    Icon: 'icons-menu-account-box',
  },
  Tasks: {
    Name: 'Tasks',
    Icon: 'icons-menu-epic',
  },
};

// User Data
// User Data
const getUserData = () =>
{
  let personId = 0;
  let userName = 'Test User';
  let userImage = 'Images/user.png?1.0.21.8';
  try
  {
    const data = sessionStorage.getItem('C2_data');
    if (data)
    {
      const parsed = JSON.parse(data);
      personId = parseInt(parsed.personid) || 0;
      userName = parsed.fullname || 'Test User';
      if (parsed.imagename) userImage = parsed.imagename;
    }
  }
  catch
  {
    /* ignore */
  }
  return {
    imageProfile: userImage,
    personName: userName,
    PersonId: personId,
    UserName: userName,
    ImageName: userImage,
  };
};

// Section configuration mapping API Name to display title and icon
const SECTION_CONFIG: Record<string, { title: string; icon: React.ReactNode; order: number }> = {
  'My Cards': { title: 'Assigned to me', icon: <User className="w-4 h-4" />, order: 1 },
  'My Cards Approver': { title: 'Approver is me', icon: <CheckSquare className="w-4 h-4" />, order: 2 },
  'My Cards Tags': { title: 'Tagged me', icon: <Tag className="w-4 h-4" />, order: 3 },
  'My Cards Team': { title: 'Your Team', icon: <Users className="w-4 h-4" />, order: 4 },
};

interface MyCardsSectionProps {
  searchText: string;
  /** Incremented by the parent to trigger a background refresh */
  refreshToken?: number;
  viewAsPersonId?: string;
}

interface DashboardGroup {
  id: number;
  name: string;
  title: string;
  icon: React.ReactNode;
  conversations: ConversationDetailType[];
  order: number;
}

import { useInfoWindow } from '../../../../contexts/InfoWindowContext';

// ... (existing imports)

export const MyCardsSection: React.FC<MyCardsSectionProps> = ({ searchText, refreshToken }) =>
{
  const socketConnectionId = useSocketConnectionId();
  const [showCompleted, setShowCompleted] = useState<boolean>(readShowCompleted);
  const [loading, setLoading] = useState(true);
  const [groups, setGroups] = useState<DashboardGroup[]>([]);
  const { openInfoWindow } = useInfoWindow();

  // Only the latest request may update the view (avoids stale ShowCompleted responses)
  const requestIdRef = useRef(0);

  useEffect(() =>
  {
    try
    {
      localStorage.setItem(SHOW_COMPLETED_STORAGE_KEY, String(showCompleted));
    }
    catch
    {
      /* ignore */
    }
  }, [showCompleted]);

  const handleShowCompletedChange = (checked: boolean) =>
  {
    setShowCompleted(checked);
    // Hide completed cards immediately instead of waiting for the reload
    if (!checked) setGroups(prev => hideCompleted(prev));
  };

  // Configuration state
  const [api] = useState(() => getApiConfig());
  const [config] = useState(() => getAppConfig());

  useEffect(() =>
  {
    loadHomeConversations();
  }, [searchText, showCompleted]);

  // Reload when a socket refresh notification arrives from the parent
  useEffect(() =>
  {
    if (refreshToken && refreshToken > 0) loadHomeConversations();
  }, [refreshToken]);

  // Recargar cuando cambien los layers ocultos
  useEffect(() =>
  {
    const handleLayersChanged = () =>
    {
      loadHomeConversations();
    };
    window.addEventListener('layersToHideChanged', handleLayersChanged);
    return () => window.removeEventListener('layersToHideChanged', handleLayersChanged);
  }, [searchText, showCompleted]);

  useEffect(() =>
  {
    const unsub = conversationBus.on('redflag-updated', ({ conversationId, isRedFlag }) =>
    {
      setGroups(prev =>
        prev.map(group => ({
          ...group,
          conversations: group.conversations.map(conv =>
            conv.ConversationId === conversationId ? { ...conv, IsRedFlag: isRedFlag } : conv,
          ),
        })),
      );
    });
    return unsub;
  }, []);

  // Init: Load from cache immediately
  useEffect(() =>
  {
    const parsed = readViewCache<{ showCompleted?: boolean; groups?: any[] }>(MYCARDS_CACHE_KEY);
    // Ignore snapshots taken with a different Show Completed mode (or the legacy array format)
    if (
      parsed &&
      parsed.showCompleted === showCompleted &&
      Array.isArray(parsed.groups) &&
      parsed.groups.length > 0
    )
    {
      // Restore React icons from SECTION_CONFIG; drop groups no longer in config
      const restored: DashboardGroup[] = parsed.groups
        .filter((g: any) => SECTION_CONFIG[g.name] !== undefined)
        .map((g: any) => ({
          ...g,
          icon: SECTION_CONFIG[g.name]!.icon,
        }));
      setGroups(showCompleted ? restored : hideCompleted(restored));
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- mount-only cache restore

  const loadHomeConversations = async () =>
  {
    const requestId = ++requestIdRef.current;
    try
    {
      // Only set loading if no data
      setLoading(groups.length > 0 ? false : true);

      // Get PersonId from authStorage (logged in user)
      const personId = authStorage.getPersonId() || '0';

      const response = await homeService.getHomeConversations({
        PageNumber: 1,
        ShowCompleted: showCompleted ? 1 : 0,
        PageSize: 100,
        Me: personId,
        LayersToHide: (() =>
        {
          try
          {
            const arr = JSON.parse(sessionStorage.getItem('C2_LayersToHide') || '[]');
            return arr.length ? arr.join(',') : '';
          }
          catch
          {
            return '';
          }
        })(),
      });

      // Map the response to our groups structure using SECTION_CONFIG
      // Groups not present in SECTION_CONFIG are excluded
      const mappedGroups: DashboardGroup[] = response
        .filter(
          (group: DashboardGroupConversationsResponse) => SECTION_CONFIG[group.Name] !== undefined,
        )
        .map((group: DashboardGroupConversationsResponse) =>
        {
          const sectionConfig = SECTION_CONFIG[group.Name]!;

          return {
            id: group.DashboardGroupId,
            name: group.Name,
            title: sectionConfig.title,
            icon: sectionConfig.icon,
            conversations: group.Conversations || [],
            order: sectionConfig.order,
          };
        })
        .sort((a, b) => a.order - b.order); // Sort by order

      // A newer request (e.g. Show Completed toggled again) owns the view
      if (requestId !== requestIdRef.current) return;

      const visibleGroups = showCompleted ? mappedGroups : hideCompleted(mappedGroups);
      setGroups(visibleGroups);

      // Icon is restored from SECTION_CONFIG on read; mode tag keeps snapshots from leaking across modes
      writeViewCache(MYCARDS_CACHE_KEY, {
        showCompleted,
        groups: visibleGroups.map(({ icon: _icon, ...group }) => ({
          ...group,
          conversations: (group.conversations || []).slice(0, MYCARDS_CACHE_LIMIT),
        })),
      });
    }
    catch (error)
    {
      console.error('Error loading home conversations:', error);
    }
    finally
    {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  };

  // Build the request object for ConversationDetailsView with isViewCard=false for title view
  const buildRequest = (conversationId: number, conversation?: ConversationDetailType) => ({
    api: api,
    enableLoading: false,
    showErrorMessage: true,
    isInfoWindow: false,
    isInfowindowViewCard: false,
    hasArchivedFilter: false,
    isConversationDetailsView: true,
    hideCustomFields: true,
    isViewCard: false, // Title view mode
    hideActionMenu: true, // Hide 3-dots menu in Home columns
    isHomeView: true,
    config: config,
    userData: getUserData(),
    storageData: getStorageData(),
    params: {
      ConversationId: conversationId,
      SocketConnectionId: socketConnectionId || undefined,
      IsFromProject: false,
      ConfigMenus: getConfigMenus(),
      ShowRefreshButton: true,
    },
    functions: {
      onLoadDataCallback: () => {},
      onSendError: () => {},
      loadingBarStart: () => {},
      loadingBarComplete: () => {},
      openInfoWindow: (conv: any) =>
      {
        // Callers pass either a plain id or the conversation object
        const id = typeof conv === 'number' ? conv : conv?.ConversationId;
        if (id) openInfoWindow(Number(id));
      },
      attachReactFunctions: () => {},
      onUpdateConversation: () => {},
      onAddReply: () => {},
      onUpdateReply: () => {},
      onDeleteReply: (_e: unknown, callback: (() => void) | undefined) =>
      {
        if (callback) callback();
      },
      onCompleteToggle: () => {},
      markRepost: () => {},
      markFavorite: () => {},
      markRedflag: () => {},
      editReply: () => {},
      deleteReply: () => {},
      archiveConversation: (_e: unknown, _conv: unknown, callback: (() => void) | undefined) =>
      {
        if (callback) callback();
      },
      destroy: () => {},
      saveSearchResult: () => {},
      copyTo: () => {},
      onReplyDeleted: () => {},
      onCreateItemFromCustomType: () => {},
      onItemConvertTo: () => {},
      onChangedProject: () => {},
      onChangedSecurityGroups: () => {},
      getContactCard: () => {},
      showRecentlyNotification: () => {},
      onDuplicateItem: () => {},
    },
    realItemTypes: realItemTypes,
    conversation: conversation
      ? ({ ...conversation, id: String(conversationId) } as any)
      : undefined,
  });

  if (loading)
  {
    return (
      <div className="mycards-section h-full flex flex-col">
        <div className="flex-1 overflow-y-auto">
          <div className="flex items-center justify-between mb-2">
            <div className="h-4 bg-gray-200 rounded w-24 animate-pulse"></div>
            <div className="h-4 bg-gray-200 rounded w-20 animate-pulse"></div>
          </div>
          <div className="flex flex-col gap-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="bg-white rounded shadow-sm">
                <div className="h-4 bg-gray-200 rounded w-32 mb-2 animate-pulse"></div>
                <div className="flex flex-col gap-[2px]">
                  <div className="h-8 bg-gray-100 rounded animate-pulse"></div>
                  <div className="h-8 bg-gray-100 rounded animate-pulse"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mycards-section h-full flex flex-col">
      {/* Sections */}
      <div className="mycards-groups-container flex flex-col gap-3 flex-1 overflow-y-auto">
        {groups.map((group, index) =>
          group.conversations.length > 0 ? (
            <div key={group.id} className="mycards-group">
              {/* Section Header - with checkbox on first section */}
              <div className="flex items-center justify-between max-h-56">
                <div className="flex items-center gap-2">
                  <span className="text-gray-500">{group.icon}</span>
                  <h3 className="text-sm font-bold text-gray-500">{group.title}</h3>
                </div>
                {/* Show Completed checkbox only on first section */}
                {index === 0 && (
                  <div className="flex items-center gap-1">
                    <span className="corgee_com_checkbox" style={{ margin: 0 }}>
                      <input
                        id="mycards-show-completed"
                        type="checkbox"
                        checked={showCompleted}
                        onChange={e => handleShowCompletedChange(e.target.checked)}
                      />
                      <label
                        htmlFor="mycards-show-completed"
                        className="conversationCheckbox cursor-pointer"
                      ></label>
                    </span>
                    <label
                      htmlFor="mycards-show-completed"
                      className="text-xs text-gray-600 dark:text-[#a9a093] cursor-pointer mb-0 mt-[3px]"
                    >
                    Show Completed
                    </label>
                  </div>
                )}
              </div>

              {/* Conversations List — fixed height so all sections are the same size */}
              <div
                className="bg-gray-100 mycards-conversations-list-container rounded shadow-sm"
                style={{ overflowY: 'auto', height: 'auto', maxHeight: '200px' }}
              >
                {group.conversations.length === 0 ? (
                  <div className="h-full flex items-center justify-center text-gray-400">
                    <p className="text-sm">No cards in this section</p>
                  </div>
                ) : (
                  <ul className="mycards-conversation-list list-none p-0 m-0 flex flex-col gap-[2px]">
                    {group.conversations.map(conv => (
                      <li
                        key={conv.ConversationId}
                        className="mycards-conversation-item flex-shrink-0"
                      >
                        <ConversationDetailsView request={buildRequest(conv.ConversationId, conv)} />
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          ) : <></>) }
      </div>
    </div>
  );
};
